import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Share2, MessageSquare, QrCode, Copy } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/paylasma")({
  component: Page,
});

type ShareRow = {
  id: string;
  listing_id: string | null;
  channel: string;
  created_at: string;
};

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  qr: "QR kod",
  kopyalama: "Link kopyalama",
  telegram: "Telegram",
  sistem: "Sistem paylaşması",
};

const CHANNEL_ICON: Record<string, React.ElementType> = {
  whatsapp: MessageSquare,
  qr: QrCode,
  kopyalama: Copy,
  telegram: Share2,
};

function Page() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-shares"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("share_events")
        .select("id, listing_id, channel, created_at")
        .order("created_at", { ascending: false })
        .limit(3000);
      if (error) throw error;
      return (data ?? []) as ShareRow[];
    },
  });

  const rows = data ?? [];
  const byChannel = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.channel] = (acc[r.channel] ?? 0) + 1;
    return acc;
  }, {});

  const byListing = [...rows.reduce<Map<string, number>>((m, r) => {
    if (!r.listing_id) return m;
    m.set(r.listing_id, (m.get(r.listing_id) ?? 0) + 1);
    return m;
  }, new Map())]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15);

  return (
    <div>
      <AdminPageHeader
        title="Paylaşma statistikası"
        description="Elanların WhatsApp, QR kod, link kopyalama və digər kanallarda paylaşılması"
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <Share2 className="size-4" /> Ümumi paylaşma
          </div>
          <p className="mt-2 text-2xl font-extrabold">{isLoading ? "…" : rows.length}</p>
        </div>
        {["whatsapp", "qr", "kopyalama"].map((ch) => {
          const Icon = CHANNEL_ICON[ch] ?? Share2;
          return (
            <div key={ch} className="rounded-2xl border border-border bg-card p-4 shadow-card">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Icon className="size-4" /> {CHANNEL_LABEL[ch]}
              </div>
              <p className="mt-2 text-2xl font-extrabold">{byChannel[ch] ?? 0}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
          <p className="mb-3 text-sm font-bold">Kanallar üzrə</p>
          {Object.keys(byChannel).length === 0 ? (
            <p className="text-xs text-muted-foreground">Məlumat yoxdur</p>
          ) : (
            <ul className="grid gap-2">
              {Object.entries(byChannel)
                .sort((a, b) => b[1] - a[1])
                .map(([ch, count]) => {
                  const pct = rows.length ? Math.round((count / rows.length) * 100) : 0;
                  return (
                    <li key={ch} className="flex items-center gap-3 text-xs">
                      <span className="w-32 shrink-0 font-semibold">
                        {CHANNEL_LABEL[ch] ?? ch}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-16 text-right font-bold">
                        {count} ({pct}%)
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
          <p className="mb-3 text-sm font-bold">Ən çox paylaşılan elanlar</p>
          {byListing.length === 0 ? (
            <p className="text-xs text-muted-foreground">Məlumat yoxdur</p>
          ) : (
            <ul className="grid gap-1.5 text-xs">
              {byListing.map(([id, count]) => (
                <li key={id} className="flex items-center justify-between gap-2">
                  <a
                    href={`/elan/${id}`}
                    className="truncate font-medium text-primary hover:underline"
                  >
                    {id}
                  </a>
                  <span className="shrink-0 font-bold">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
